/**
 * ImageSelector Component
 * 
 * A unified image selection component with 3 options:
 * 1. Browse - Choose from available images in database (DEFAULT)
 * 2. Upload - Upload from device
 * 3. URL - Paste image URL
 * 
 * Usage:
 * <ImageSelector
 *   value={imageUrl}
 *   onChange={(url) => setImageUrl(url)}
 *   label="Product Image"
 * />
 */

import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { HiPhotograph, HiUpload, HiLink, HiX, HiCheck, HiArrowCircleRight } from 'react-icons/hi';
import ImageGalleryPicker from './ImageGalleryPicker';
import { adminFetch, adminHeaders } from '../lib/adminApi';
import ImageSelectorCropModal from './ImageSelectorCropModal';
import ImageDetailFitToggle from './ImageDetailFitToggle';
import { normalizeDetailFit, type DetailFit } from '../utils/imageDisplay';

// API URL
const API_URL = import.meta.env.VITE_API_URL || '';

type TabType = 'browse' | 'upload' | 'url';

interface ImageSelectorProps {
  /** Current image URL value */
  value: string;
  /** Callback when image changes */
  onChange: (url: string) => void;
  /** Label for the field */
  label?: string;
  /** Placeholder text for URL input */
  placeholder?: string;
  /** Help text below the selector */
  helpText?: string;
  /** Show preview of selected image */
  showPreview?: boolean;
  /** Preview image height class */
  previewHeight?: string;
  /** Allow clearing the image */
  allowClear?: boolean;
  /** Disabled state */
  disabled?: boolean;
  /** Default tab (defaults to 'browse') */
  defaultTab?: TabType;
  /**
   * Preview frame aspect so Tammy sees how object-cover will crop.
   * menu ≈ 16/9 cards, gallery ≈ square, hero ≈ wide
   */
  previewAspect?: 'square' | 'video' | 'wide';
}

export default function ImageSelector({
  value,
  onChange,
  label,
  placeholder = 'https://example.com/image.jpg',
  helpText,
  showPreview = true,
  previewHeight = 'h-48',
  allowClear = true,
  disabled = false,
  defaultTab = 'browse',
  previewAspect = 'video',
}: ImageSelectorProps) {
  const [activeTab, setActiveTab] = useState<TabType>(defaultTab);
  const [showGalleryPicker, setShowGalleryPicker] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [customName, setCustomName] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  /** Crop modal: object URL + original file */
  const [cropModal, setCropModal] = useState<{ src: string; file: File } | null>(null);
  const [objectPosition, setObjectPosition] = useState<string | null>(null);
  const [detailFit, setDetailFit] = useState<DetailFit>('contain');
  const [rotating, setRotating] = useState(false);

  const aspectRatio =
    previewAspect === 'square' ? 1 : previewAspect === 'wide' ? 21 / 9 : 16 / 9;

  const previewAspectClass =
    previewAspect === 'square'
      ? 'aspect-square'
      : previewAspect === 'wide'
        ? 'aspect-[21/9]'
        : 'aspect-video';

  // Load saved focal point when value changes
  useEffect(() => {
    if (!value || !value.startsWith('http')) {
      setObjectPosition(null);
      return;
    }
    const u = API_URL ? `${API_URL}/api/image-display-settings?url=${encodeURIComponent(value)}` : `/api/image-display-settings?url=${encodeURIComponent(value)}`;
    adminFetch(u)
      .then((r) => r.json())
      .then((d) => {
        setObjectPosition(d.object_position || '50% 50%');
        setDetailFit(normalizeDetailFit(d.detail_fit));
      })
      .catch(() => setObjectPosition(null));
  }, [value]);

  const saveDisplaySettings = async (next: { object_position?: string; detail_fit?: DetailFit }) => {
    const putUrl = API_URL ? `${API_URL}/api/image-display-settings` : '/api/image-display-settings';
    await adminFetch(putUrl, {
      method: 'PUT',
      headers: adminHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({
        image_url: value,
        object_position: next.object_position ?? objectPosition ?? '50% 50%',
        detail_fit: next.detail_fit ?? detailFit,
      }),
    });
  };

  const performUpload = async (file: File | Blob, fileName = 'upload.jpg') => {
    setUploading(true);
    setUploadError(null);
    setUploadSuccess(false);
    try {
      const formData = new FormData();
      const uploadFile = file instanceof File ? file : new File([file], fileName, { type: 'image/jpeg' });
      formData.append('image', uploadFile);
      if (customName.trim()) {
        formData.append('customName', customName.trim());
      }
      const uploadUrl = API_URL ? `${API_URL}/api/upload` : '/api/upload';
      const response = await adminFetch(uploadUrl, { method: 'POST', body: formData });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Upload failed');
      }
      const data = await response.json();
      if (!data.url) throw new Error('Server did not return image URL');
      onChange(data.url);
      setUploadSuccess(true);
      setCustomName('');
      setTimeout(() => setUploadSuccess(false), 3000);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to upload image';
      setUploadError(`Upload failed: ${errorMessage}`);
      setTimeout(() => setUploadError(null), 5000);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (cropModal?.src) URL.revokeObjectURL(cropModal.src);
      setCropModal(null);
    }
  };

  // File chosen → open crop modal (cropper uses same aspect as preview)
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setUploadError('Please select an image file');
      setTimeout(() => setUploadError(null), 5000);
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      setUploadError('Image must be smaller than 20MB');
      setTimeout(() => setUploadError(null), 5000);
      return;
    }
    const src = URL.createObjectURL(file);
    setCropModal({ src, file });
  };

  /** Supabase storage path segment → filename for rotate API */
  const supabaseFilename = (url: string): string | null => {
    const m = url.match(/\/product-images\/([^?]+)/);
    return m ? decodeURIComponent(m[1]) : null;
  };

  const handleRotateInPlace = async () => {
    const fn = value ? supabaseFilename(value) : null;
    if (!fn) return;
    setRotating(true);
    try {
      const path = `/api/upload/${encodeURIComponent(fn)}/rotate?degrees=90`;
      const res = await adminFetch(API_URL ? `${API_URL}${path}` : path, { method: 'POST' });
      if (!res.ok) throw new Error('Rotate failed');
      onChange(value + (value.includes('?') ? '&' : '?') + 't=' + Date.now());
      setUploadSuccess(true);
      setTimeout(() => setUploadSuccess(false), 2000);
    } catch {
      setUploadError('Rotate failed. Use All Images → Rotate 90°.');
      setTimeout(() => setUploadError(null), 5000);
    } finally {
      setRotating(false);
    }
  };

  const handlePreviewClick = async (e: React.MouseEvent<HTMLImageElement>) => {
    if (!value.startsWith('http')) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    const pos = `${Math.round(x)}% ${Math.round(y)}%`;
    setObjectPosition(pos);
    try {
      await saveDisplaySettings({ object_position: pos });
    } catch {
      // non-fatal — table missing used to fail silently; retry after deploy
    }
  };

  const handleDetailFitChange = async (fit: DetailFit) => {
    setDetailFit(fit);
    if (!value.startsWith('http')) return;
    try {
      await saveDisplaySettings({ detail_fit: fit });
    } catch {
      setUploadError('Could not save photo size. Try again or ask for help.');
      setTimeout(() => setUploadError(null), 5000);
    }
  };

  // Handle image selection from gallery
  const handleGallerySelect = (imageUrl: string) => {
    onChange(imageUrl);
    setShowGalleryPicker(false);
    setUploadSuccess(true);
    setTimeout(() => setUploadSuccess(false), 3000);
  };

  // Clear image
  const handleClear = () => {
    onChange('');
  };

  const tabs: Array<{ id: TabType; label: string; icon: typeof HiPhotograph }> = [
    { id: 'browse', label: 'Browse Images', icon: HiPhotograph },
    { id: 'upload', label: 'Upload', icon: HiUpload },
    { id: 'url', label: 'Paste URL', icon: HiLink },
  ];

  return (
    <div className="space-y-3">
      {/* Label */}
      {label && (
        <label className="block text-sm font-medium text-gray-700">
          {label}
        </label>
      )}

      {/* Success Message */}
      {uploadSuccess && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2">
          <HiCheck className="w-5 h-5 text-green-600" />
          <span className="text-sm text-green-700">Image selected successfully!</span>
        </div>
      )}

      {/* Error Message */}
      {uploadError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
          <HiX className="w-5 h-5 text-red-600" />
          <span className="text-sm text-red-700">{uploadError}</span>
        </div>
      )}

      {/* Tab Buttons */}
      <div className="flex gap-1 border-b border-gray-200">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              disabled={disabled}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors
                ${activeTab === tab.id
                  ? 'border-purple-600 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
                ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
              `}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <div className="pt-2">
        {/* Browse Tab - Default */}
        {activeTab === 'browse' && (
          <div className="space-y-3">
            <button
              type="button"
              onClick={() => setShowGalleryPicker(true)}
              disabled={disabled}
              className="w-full flex items-center justify-center gap-2 px-4 py-4 
                         border-2 border-dashed border-purple-300 rounded-lg cursor-pointer
                         hover:border-purple-400 hover:bg-purple-50 transition-colors
                         bg-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <HiPhotograph className="w-6 h-6 text-purple-600" />
              <span className="text-gray-600">
                <span className="text-purple-600 font-medium">Browse</span> from your existing images
              </span>
            </button>
            <p className="text-xs text-gray-500 text-center">
              Choose from menu items, gallery photos, or collection banners
            </p>
          </div>
        )}

        {/* Upload Tab */}
        {activeTab === 'upload' && (
          <div className="space-y-3">
            {/* Custom filename input - encouraged for organization */}
            <div className={`p-3 rounded-lg border-2 ${
              customName.trim() 
                ? 'border-green-200 bg-green-50' 
                : 'border-amber-200 bg-amber-50'
            }`}>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                📝 Name this image
                <span className="text-amber-600 ml-1">(recommended)</span>
              </label>
              <input
                type="text"
                value={customName}
                onChange={(e) => setCustomName(e.target.value)}
                placeholder="e.g., chocolate-birthday-cake, valentine-cookies"
                disabled={disabled || uploading}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg
                           focus:ring-2 focus:ring-purple-500 focus:border-purple-500
                           disabled:opacity-50 disabled:cursor-not-allowed bg-white"
              />
              {!customName.trim() ? (
                <p className="text-xs text-amber-700 mt-2 flex items-start gap-1">
                  <span>⚠️</span>
                  <span>Without a name, images get confusing timestamps. 
                  A simple name like "red-velvet-cake" makes it easy to find later!</span>
                </p>
              ) : (
                <p className="text-xs text-green-700 mt-2 flex items-center gap-1">
                  <span>✓</span>
                  <span>Great! Your image will be saved as: <strong>{customName.trim().toLowerCase().replace(/[^a-z0-9.-]/g, '-')}.webp</strong></span>
                </p>
              )}
            </div>
            
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileUpload}
              disabled={disabled || uploading}
              className="hidden"
              id="image-selector-upload"
            />
            {uploading ? (
              <div className="flex items-center justify-center gap-2 px-4 py-4 
                             border-2 border-dashed border-gray-300 rounded-lg">
                <div className="w-5 h-5 border-2 border-purple-600 border-t-transparent rounded-full animate-spin" />
                <span className="text-purple-600 font-medium">Uploading...</span>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={disabled}
                className="w-full flex items-center justify-center gap-2 px-4 py-4 
                           border-2 border-dashed border-gray-300 rounded-lg cursor-pointer
                           hover:border-purple-400 hover:bg-purple-50 transition-colors
                           bg-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <HiUpload className="w-6 h-6 text-purple-600" />
                <span className="text-gray-600">
                  <span className="text-purple-600 font-medium">Click to upload</span> from your device
                </span>
              </button>
            )}
            <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm">
              <span className="text-blue-500 mt-0.5">ℹ️</span>
              <p className="text-blue-700">
                <strong>Tip:</strong> Use descriptive names like "chocolate-drip-cake" or "easter-sugar-cookies". 
                Images auto-optimize. Supports iPhone photos (HEIC). Max 20MB.
              </p>
            </div>
          </div>
        )}

        {/* URL Tab */}
        {activeTab === 'url' && (
          <div className="space-y-3">
            <input
              type="text"
              value={value}
              onChange={(e) => onChange(e.target.value)}
              placeholder={placeholder}
              disabled={disabled}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg
                         focus:ring-2 focus:ring-purple-500 focus:border-purple-500
                         disabled:opacity-50 disabled:cursor-not-allowed"
            />
            <p className="text-xs text-gray-500">
              Paste a direct link to an image from the web
            </p>
          </div>
        )}
      </div>

      {/* Image Preview — contextual aspect + focal point click + rotate link */}
      {showPreview && value && (
        <div className="relative inline-block mt-3 max-w-md w-full">
          {/* previewHeight reserved for callers; aspect wrapper drives frame */}
          <div
            className={`w-full overflow-hidden rounded-lg border bg-gray-100 ${previewAspectClass}`}
            data-preview-height={previewHeight}
          >
            <img
              src={value}
              alt="Preview"
              className="w-full h-full object-cover cursor-crosshair"
              style={objectPosition ? { objectPosition } : undefined}
              title="Click to set focal point (where the subject stays in frame)"
              onClick={handlePreviewClick}
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          </div>
          {value.startsWith('http') && (
            <p className="text-xs text-gray-500 mt-1">
              This frame is how menu cards crop. Click the photo to set the focal point.
            </p>
          )}
          {value.startsWith('http') && (
            <ImageDetailFitToggle value={detailFit} onChange={handleDetailFitChange} />
          )}
          {value.startsWith('http') && detailFit === 'contain' && (
            <div className="mt-2 p-2 bg-gray-50 rounded-lg border">
              <p className="text-xs text-gray-500 mb-1">Product page preview (full photo)</p>
              <img
                src={value}
                alt="Product page preview"
                className="max-h-40 w-auto mx-auto object-contain"
              />
            </div>
          )}
          {supabaseFilename(value) && (
            <div className="flex flex-wrap gap-2 mt-2">
              <button
                type="button"
                onClick={handleRotateInPlace}
                disabled={rotating}
                className="text-xs flex items-center gap-1 px-2 py-1 rounded border border-purple-200 text-purple-700 hover:bg-purple-50"
              >
                <HiArrowCircleRight className="w-4 h-4" />
                {rotating ? 'Rotating…' : 'Rotate 90°'}
              </button>
              <Link
                to="/admin/images"
                className="text-xs px-2 py-1 rounded border border-gray-200 text-gray-700 hover:bg-gray-50"
              >
                All Images
              </Link>
            </div>
          )}
          {allowClear && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full
                         flex items-center justify-center hover:bg-red-600 transition-colors"
              title="Remove image"
            >
              <HiX className="w-4 h-4" />
            </button>
          )}
        </div>
      )}

      {/* Help Text */}
      {helpText && (
        <p className="text-xs text-gray-500">{helpText}</p>
      )}

      {/* Image Gallery Picker Modal */}
      <ImageGalleryPicker
        isOpen={showGalleryPicker}
        onClose={() => setShowGalleryPicker(false)}
        onSelect={handleGallerySelect}
        currentImageUrl={value}
      />

      {cropModal && (
        <ImageSelectorCropModal
          imageSrc={cropModal.src}
          aspect={aspectRatio}
          originalFile={cropModal.file}
          onClose={() => {
            URL.revokeObjectURL(cropModal.src);
            setCropModal(null);
            if (fileInputRef.current) fileInputRef.current.value = '';
          }}
          onConfirm={(blob) => performUpload(blob, 'cropped.jpg')}
          onSkipUpload={(file) => performUpload(file)}
        />
      )}
    </div>
  );
}
